import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { buildApiUrl } from '../utils/api';

const AdventuresPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popularity');
  
  // Use professional adventures from database
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfessionalAdventures();
  }, []);

  const fetchProfessionalAdventures = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl('/adventures'));
      
      if (response.ok) {
        const data = await response.json();
        setAdventures(data);
      } else {
        // Fallback to hook if professional API fails
        console.log('Professional API failed, using fallback');
        setError('Failed to load adventures');
      }
    } catch (err) {
      console.error('Error fetching professional adventures:', err);
      setError('Failed to load adventures');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Adventures' },
    { value: 'scuba-diving', label: 'Scuba Diving' },
    { value: 'snorkeling', label: 'Snorkeling' },
    { value: 'camping', label: 'Camping' },
    { value: 'combined', label: 'Combined Adventure' }
  ];

  const durations = [
    { value: 'all', label: 'Any Duration' },
    { value: '1day', label: '1 Day' },
    { value: 'weekend', label: 'Weekend' },
    { value: 'multiday', label: 'Multi-day' }
  ];

  const skillLevels = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'all-levels', label: 'All Levels' }
  ];

  const sortOptions = [
    { value: 'popularity', label: 'Most Popular' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'duration', label: 'Duration' }
  ];

  const filteredAndSortedAdventures = useMemo(() => {
    if (!adventures || adventures.length === 0) return [];
    
    let filtered = adventures.filter(adventure => {
      // Category filter
      if (selectedCategory !== 'all') {
        if (adventure.category !== selectedCategory) return false;
      }

      // Skill level filter
      if (selectedSkillLevel !== 'all') {
        if (adventure.skill_level !== selectedSkillLevel) return false;
      }

      // Duration filter
      if (selectedDuration !== 'all') {
        const duration = adventure.duration ? adventure.duration.toLowerCase() : '';
        if (selectedDuration === '1day' && !duration.includes('1 day') && !duration.includes('day')) return false;
        if (selectedDuration === 'weekend' && !duration.includes('2 days') && !duration.includes('weekend')) return false;
        if (selectedDuration === 'multiday' && !duration.includes('3 days') && !duration.includes('multi')) return false;
      }

      return true;
    });

    // Sort adventures
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.price_num || 0) - (b.price_num || 0);
        case 'price-high':
          return (b.price_num || 0) - (a.price_num || 0);
        case 'duration':
          return (a.duration || '').localeCompare(b.duration || '');
        default: // popularity
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0); // Featured adventures first
      }
    });

    return filtered;
  }, [adventures, selectedCategory, selectedDuration, selectedSkillLevel, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading adventures: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-ocean-600 text-white px-6 py-2 rounded-lg hover:bg-ocean-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">All Adventures</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              Discover all our transformative adventures with complete details, inclusions, and itineraries. 
              Find the perfect experience for your skill level and interests.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="font-semibold">{adventures.length}</span> Unique Adventures
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="font-semibold">All Skill Levels</span> Welcome
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="font-semibold">PADI Certified</span> Instruction
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters Section - Only sticky on large screens */}
      <section className="py-8 bg-white border-b border-slate-200 lg:sticky lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4">
              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              {/* Duration Filter */}
              <select
                value={selectedDuration}
                onChange={(e) => setSelectedDuration(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
              >
                {durations.map(duration => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>

              {/* Skill Level Filter */}
              <select
                value={selectedSkillLevel}
                onChange={(e) => setSelectedSkillLevel(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
              >
                {skillLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-slate-600 font-medium">
                {filteredAndSortedAdventures.length} adventures found
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Adventures Grid */}
      <section className="py-16" style={{ backgroundColor: '#0b4c71' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredAndSortedAdventures.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🏊‍♂️</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">No adventures found</h3>
              <p className="text-slate-600 mb-8">Try adjusting your filters to see more options</p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedDuration('all');
                  setSelectedSkillLevel('all');
                }}
                className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredAndSortedAdventures.map((adventure, index) => (
                <motion.div
                  key={adventure.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card-elegant elegant-shadow-lg rounded-3xl overflow-hidden h-full flex flex-col group"
                >
                  {/* Adventure Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={adventure.hero_image || adventure.image}
                      alt={adventure.hero_image_alt || adventure.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex justify-between items-center">
                        <span className="bg-coral-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {adventure.duration}
                        </span>
                        <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                          {adventure.groupSize}
                        </span>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        adventure.skill_level === 'beginner' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {adventure.skill_level?.charAt(0).toUpperCase() + adventure.skill_level?.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="p-6 bg-white flex flex-col flex-grow">
                    {/* Title and Price */}
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{adventure.title}</h3>
                    <p className="text-coral-600 font-bold text-lg mb-4">{adventure.price_display || `$${adventure.base_price}`}</p>
                    
                    {/* Key Features */}
                    <div className="space-y-2 mb-6 flex-grow">
                      {(adventure.highlights || adventure.included_items || []).slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <div className="w-2 h-2 bg-coral-400 rounded-full mr-3 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {(adventure.tags || []).slice(0, 2).map((tag, idx) => (
                        <span
                          key={idx}
                          className="bg-slate-100 text-slate-600 px-2 py-1 rounded-lg text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-3 mt-auto">
                      <Link 
                        to={`/adventures/${adventure.slug}`}
                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold text-center transition-all duration-300 hover:shadow-md"
                      >
                        Learn More
                      </Link>
                      <Link 
                        to="/book"
                        className="flex-1 btn-elegant py-3 text-center text-sm"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-coral-500 to-coral-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Can't Decide?</h2>
          <p className="text-xl text-coral-100 mb-8 max-w-3xl mx-auto">
            Not sure which adventure is right for you? Ahmed can help you choose the perfect experience 
            based on your interests, skill level, and goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-coral-600 hover:bg-coral-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Get Personal Recommendations
            </Link>
            <Link
              to="/weekend"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Try Our Weekend Experience
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdventuresPage;